'use strict';

/**
 * Pict-Provider-EntityAPI
 * -----------------------
 * Generic browser-side CRUD client for one meadow-endpoints entity. Registered
 * once per SDLC entity (Vision, Module, Feature, WorkItem) with a different
 * EntityName / PluralRoute / IDField. Tenancy is enforced entirely server-side
 * (IDCustomer is stamped + filtered there), so the client never sends it.
 *
 * meadow-endpoints route shapes (note the pluralized list routes):
 *   list   GET    /1.0/<Plural>/:Begin/:Cap                   -> JSON array
 *   page   GET    /1.0/<Plural>/FilteredTo/:Filter/:Begin/:Cap -> filtered, sorted and paged, in one call
 *   count  GET    /1.0/<Plural>/Count  |  /Count/FilteredTo/:Filter -> { Count: N }
 *   create POST   /1.0/<Entity>                                -> created row
 *   update PUT    /1.0/<Entity>                                -> updated row (body carries the id)
 *   delete DELETE /1.0/<Entity>/:id
 *
 * ONE PAGE OF THE SERVER'S ANSWER, NOT A GROWING HEAP (WI #433). This used to fetch the first 250 rows
 * and hand them to a view that sorted and filtered that array in the browser. Sorting 1,075 blueprints
 * descending by mark put 250 at the top and 1 at the bottom, because 250 was the largest thing the
 * client had ever been shown, and nothing on screen said so.
 *
 * So `List` is now exactly the server's answer for the page that was asked for, and the position is
 * held explicitly in `Page` rather than inferred from `List.length`. Inferring it is what made the old
 * loadMore() unreliable: cacheRecord() pushes an out-of-page record into the store, so the offset for
 * the next page was one row further along than the server's idea of it and a row was silently skipped.
 * cacheRecord now writes the Index alone for that reason -- lookups by id still resolve, and the page
 * stays the page.
 *
 * AppData shape this provider owns:
 *   AppData.Entities[EntityName] =
 *     { List, Index, Loading, Error, Total, HasMore, Page, PageSize, PageCount, QueryKey }
 *
 * QueryKey fingerprints the sort + filter the rows in List answer. The caller compares it to decide
 * whether it is looking at a new question (back to page 1) or a new page of the same one.
 *
 * A CALLER THAT JUST NEEDS TO RESOLVE RECORDS -- A LABEL, A PICKER -- USES resolve(), NOT list()
 * (WI #447). The detail view loads the four SDLC bases (and any relation target) to draw its pickers,
 * and used to do that with a plain list(): a request for exactly the page a list SCREEN already reads
 * from. Opening any record replaced that screen's List, reset its Page to 1, and overwrote its
 * QueryKey out from under it, so paging to page 4, opening a record, and going back silently landed
 * back on page 1. resolve() asks the server the identical question but writes only to Index, the same
 * seam cacheRecord already uses for exactly this reason; the list screen's own paging is untouched.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'EntityAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/',
	EntityName: 'Entity',
	PluralRoute: 'Entitys',
	IDField: 'IDEntity'
};

// Rows per page. The UI (PictView-EntityList) draws first / prev / page N of M / next / last against it.
const _PAGE_SIZE = 250;

// The cap on one sweep of _listAll, which walks pages until the server runs out. It exists only so a
// bug (or a table that grew a great deal between the count and the walk) cannot spin forever; the
// sweep also stops on any short page, which is the ordinary way out.
const _ALL_PAGE_LIMIT = 200;

class PictProviderEntityAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);

		this.routePrefix = this.options.RoutePrefix;
		this.entityName = this.options.EntityName;
		this.pluralRoute = this.options.PluralRoute;
		this.idField = this.options.IDField;
	}

	store()
	{
		if (!this.fable.AppData.Entities) this.fable.AppData.Entities = {};
		if (!this.fable.AppData.Entities[this.entityName])
		{
			this.fable.AppData.Entities[this.entityName] =
				{ List: [], Index: {}, Loading: false, Error: null, Total: 0, HasMore: false, Page: 1, PageSize: _PAGE_SIZE, PageCount: 1, QueryKey: '' };
		}
		return this.fable.AppData.Entities[this.entityName];
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_index(pList)
	{
		let tmpStore = this.store();
		tmpStore.List = Array.isArray(pList) ? pList : [];
		tmpStore.Index = {};
		for (let i = 0; i < tmpStore.List.length; i++)
		{
			tmpStore.Index[tmpStore.List[i][this.idField]] = tmpStore.List[i];
		}
	}

	// The count from meadow's /Count ({ Count: N }); falls back to the loaded length when it is unavailable.
	_countOf(pCountBody, pFallback)
	{
		let tmpN = pCountBody && (typeof pCountBody === 'number' ? pCountBody : pCountBody.Count);
		return (typeof tmpN === 'number' && tmpN >= 0) ? tmpN : pFallback;
	}

	// meadow answers a list as a bare array; a few wrapped shapes exist in the wild, so unwrap either.
	_rowsOf(pBody)
	{
		return Array.isArray(pBody) ? pBody : ((pBody && pBody.Records) || []);
	}

	// The read route for a window of rows: FilteredTo when there is something to filter or sort by,
	// the plain begin/cap route when there is not.
	//
	// THE FILTER IS URL-ENCODED, unlike the hand-built filters elsewhere in this app. Those carry only
	// ids and tildes, both of which survive a path segment untouched. These carry LIKE wildcards, and a
	// bare '%' makes Express' own decodeURIComponent of the route parameter throw, which is a 400 on
	// the first character typed into any search box.
	_readRoute(pFilter, pBegin, pCap)
	{
		let tmpTail = '/' + pBegin + '/' + pCap;
		if (!pFilter) { return this.pluralRoute + tmpTail; }
		return this.pluralRoute + '/FilteredTo/' + encodeURIComponent(pFilter) + tmpTail;
	}

	// The count for the SAME question the page asked. Counting the unfiltered table instead is what
	// makes a page count lie the moment a filter is on: 5 pages of "matches" over 2 pages of matches.
	_countRoute(pFilter)
	{
		if (!pFilter) { return this.pluralRoute + '/Count'; }
		return this.pluralRoute + '/Count/FilteredTo/' + encodeURIComponent(pFilter);
	}

	// A fingerprint of the question the store's rows answer, so a caller can tell a new question
	// (start at page 1) from another page of the same one.
	queryKey(pOptions)
	{
		let tmpOptions = pOptions || {};
		return (tmpOptions.All ? 'all|' : 'page|') + String(tmpOptions.Filter || '');
	}

	/**
	 * One page of records, plus the real total for the filter that page was fetched with.
	 *
	 *   list()                                    page 1, unfiltered -- what every non-list caller wants
	 *   list({ Page: 3, Filter: '<meadow-filter>' })  the third page of a filtered, sorted list
	 *   list({ All: true })                       every row, unpaged (see _listAll)
	 *
	 * List is replaced, never appended to: it is the server's answer for one page and nothing else.
	 */
	list(pOptions)
	{
		let tmpOptions = pOptions || {};
		if (tmpOptions.All) { return this._listAll(tmpOptions); }
		let tmpStore = this.store();
		let tmpPageSize = Math.max(1, Number(tmpOptions.PageSize) || _PAGE_SIZE);
		let tmpPage = Math.max(1, Number(tmpOptions.Page) || 1);
		let tmpFilter = String(tmpOptions.Filter || '');
		let tmpBegin = (tmpPage - 1) * tmpPageSize;
		tmpStore.Loading = true;
		// Both through the shared RestClient so a 401 here drives session recovery. The count is
		// best-effort: a missing /Count (or a failure) leaves the page count inferred from this page.
		let tmpRows = libFrameworkClient.getJSON(this.fable, this._url(this._readRoute(tmpFilter, tmpBegin, tmpPageSize)));
		let tmpCount = libFrameworkClient.getJSON(this.fable, this._url(this._countRoute(tmpFilter))).catch(() => null);
		return Promise.all([tmpRows, tmpCount])
			.then((pResults) =>
			{
				let tmpPageRows = this._rowsOf(pResults[0]);
				this._index(tmpPageRows);
				// The floor under the total when /Count did not answer: everything before this page,
				// plus this page. It never claims rows it has not seen.
				tmpStore.Total = this._countOf(pResults[1], tmpBegin + tmpPageRows.length);
				tmpStore.PageSize = tmpPageSize;
				tmpStore.Page = tmpPage;
				tmpStore.PageCount = Math.max(1, Math.ceil(tmpStore.Total / tmpPageSize));
				tmpStore.HasMore = tmpPage < tmpStore.PageCount;
				tmpStore.QueryKey = this.queryKey(tmpOptions);
				tmpStore.Loading = false;
				tmpStore.Error = null;
				return tmpStore.List;
			})
			.catch((pErr) =>
			{
				tmpStore.Loading = false;
				tmpStore.Error = pErr.message;
				return [];
			});
	}

	/**
	 * Every row, in one unpaged store.
	 *
	 * This is the honest fallback, not an optimisation. When the screen is asking something the filter
	 * language cannot express (see FocusQuery's Unexpressed), the server would answer a DIFFERENT
	 * question than the one on screen, so the caller fetches the whole set and filters it in the
	 * browser instead. Applying half the question to one page and paging it anyway is the exact defect
	 * WI #433 exists to fix.
	 *
	 * Sequential because each page's begin depends on how much came back before it.
	 */
	_listAll(pOptions)
	{
		let tmpStore = this.store();
		let tmpOptions = pOptions || {};
		let tmpPageSize = Math.max(1, Number(tmpOptions.PageSize) || _PAGE_SIZE);
		let tmpFilter = String(tmpOptions.Filter || '');
		let tmpAll = [];
		tmpStore.Loading = true;
		let fStep = (pPageIndex) =>
		{
			return libFrameworkClient.getJSON(this.fable, this._url(this._readRoute(tmpFilter, pPageIndex * tmpPageSize, tmpPageSize)))
				.then((pBody) =>
				{
					let tmpRows = this._rowsOf(pBody);
					tmpAll = tmpAll.concat(tmpRows);
					// A short page is the last page, whatever any count says, so a stale total can
					// never spin this.
					if (tmpRows.length < tmpPageSize) { return tmpAll; }
					if ((pPageIndex + 1) >= _ALL_PAGE_LIMIT) { return tmpAll; }
					return fStep(pPageIndex + 1);
				});
		};
		return fStep(0)
			.then(() =>
			{
				this._index(tmpAll);
				tmpStore.Total = tmpAll.length;
				tmpStore.PageSize = tmpPageSize;
				tmpStore.Page = 1;
				tmpStore.PageCount = 1;
				tmpStore.HasMore = false;
				tmpStore.QueryKey = this.queryKey(tmpOptions);
				tmpStore.Loading = false;
				tmpStore.Error = null;
				return tmpStore.List;
			})
			.catch((pErr) =>
			{
				tmpStore.Loading = false;
				tmpStore.Error = pErr.message;
				return [];
			});
	}

	// Read one record by id (meadow read-one: GET /1.0/<Entity>/<id>). Returns the record or null.
	read(pID)
	{
		// Recovery-covered read; preserves the null-on-miss contract callers rely on.
		return libFrameworkClient.getJSON(this.fable, this._url(this.entityName + '/' + encodeURIComponent(pID)))
			.then((pBody) => (pBody && pBody[this.idField]) ? pBody : null)
			.catch(() => null);
	}

	// Merge one record (e.g. a read-by-id for a record outside the current page) into the store so
	// lookups by id resolve. Used by the detail view, which resolves the open record by id rather than
	// depending on whichever page the list happens to be showing (WI #329).
	//
	// THE INDEX, NEVER THE LIST (WI #433). This used to push an out-of-page record onto List, which put
	// a row on screen that the server had not put on this page and pushed the last real row off the
	// end of it. It also broke the old loadMore(), whose next offset was List.length: one extra row in
	// the store meant one row of the next page silently skipped. List is the server's answer for the
	// page that was asked for; every id lookup reads Index, so nothing needs it to be more than that.
	cacheRecord(pRecord)
	{
		if (!pRecord) { return pRecord; }
		let tmpID = pRecord[this.idField];
		if (tmpID === null || typeof tmpID === 'undefined') { return pRecord; }
		let tmpStore = this.store();
		if (tmpStore.Index[tmpID]) { Object.assign(tmpStore.Index[tmpID], pRecord); }
		else { tmpStore.Index[tmpID] = pRecord; }
		return pRecord;
	}

	// cacheRecord, for a page of rows at once. See resolve(), its one caller.
	cacheRecords(pRows)
	{
		(pRows || []).forEach((pRow) => this.cacheRecord(pRow));
		return pRows || [];
	}

	/**
	 * One page of records merged into the Index, for a caller that needs to RESOLVE records -- a label
	 * lookup, a picker's options -- without asserting itself as the list screen's current question
	 * (WI #447).
	 *
	 * THE INDEX, NEVER LIST/PAGE/QUERYKEY, FOR THE SAME REASON AS cacheRecord (WI #433/#447). A detail
	 * view opens in front of whichever list a person was just paging through, and it needs the four SDLC
	 * bases (plus any relation target) loaded to resolve names and build its pickers. Fetching those
	 * through list() -- as it used to -- REPLACED this store's List, reset Page to 1 and overwrote
	 * QueryKey, so opening any record silently moved the list screen behind it back to page one. resolve()
	 * asks the server for the identical page list() would, but writes only to Index: the list screen's own
	 * paging state is untouched, and the caller gets the rows back directly to build a picker from.
	 */
	resolve(pOptions)
	{
		let tmpOptions = pOptions || {};
		let tmpPageSize = Math.max(1, Number(tmpOptions.PageSize) || _PAGE_SIZE);
		let tmpPage = Math.max(1, Number(tmpOptions.Page) || 1);
		let tmpFilter = String(tmpOptions.Filter || '');
		let tmpBegin = (tmpPage - 1) * tmpPageSize;
		return libFrameworkClient.getJSON(this.fable, this._url(this._readRoute(tmpFilter, tmpBegin, tmpPageSize)))
			.then((pBody) => this.cacheRecords(this._rowsOf(pBody)))
			.catch(() => []);
	}

	create(pRecord)
	{
		// EntityProvider.createEntity -> the shared RestClient's postJSON (recovery-covered).
		return libFrameworkClient.createEntity(this.fable, this.entityName, pRecord);
	}

	update(pID, pRecord)
	{
		let tmpBody = Object.assign({}, pRecord);
		tmpBody[this.idField] = pID;
		return libFrameworkClient.updateEntity(this.fable, this.entityName, tmpBody);
	}

	remove(pID)
	{
		return libFrameworkClient.deleteEntity(this.fable, this.entityName, pID).then(() => true);
	}
}

module.exports = PictProviderEntityAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
